import { Component, OnInit, ViewChild, ElementRef, OnDestroy, NgZone, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FacilityService } from '../../../shared/services/facility.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FloorService } from '../../../shared/services/floor.service';
import { DeviceMapService } from '../../../shared/services/device-map.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { PermissionService } from '../../../shared/services/permission.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { PubSubService } from '../../../shared/services/pubsub.service';
import { DeviceHealthService } from '../../../shared/services/device-health.service';
import { SimulationStateService } from '../../../shared/services/simulation-state.service';
import { SimulationTargetMappingService } from '../../../shared/services/simulation-target-mapping.service';
import { GlobalService } from '../../../shared/services/global.service';
import { DeleteModalComponent } from '../../../shared/components/delete-modal/delete-modal.component';
import { FeatherIconComponent } from '../../../shared/components/ui/feather-icon/feather-icon.component';
import { AlertFilter } from '../../../shared/data/enums';
import { RoomBoundaryLayerComponent } from '../../../shared/components/room-boundary-layer/room-boundary-layer.component';
import { FloorRoomService } from '../../../shared/services/floor-room.service';
import { FloorRoom } from '../../../shared/interface/floor-room';
interface DeviceAlert {
  id: string;
  floorName: string;
  roomName: string;
  deviceName: string;
  category: string;
  categoryClass: string;
  categoryColor: string;
  createdOn: Date;
  macId: string;
  status:string;
  date?: string;
  timeStamp: string;
  
}

@Component({
  selector: 'app-device-map',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NgSelectComponent, NgbTooltipModule, DeleteModalComponent, FeatherIconComponent, RoomBoundaryLayerComponent],
  templateUrl: './device-map.component.html',
  styleUrl: './device-map.component.scss'
})
export class DeviceMapComponent implements OnInit, OnDestroy {

  @ViewChild('canvasContainer') canvasContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('floorImage') floorImage!: ElementRef<HTMLImageElement>;
  @ViewChild('alertsContainer') alertsContainer?: ElementRef<HTMLDivElement>;
  @ViewChild(DeleteModalComponent) deleteModal!: DeleteModalComponent;

  facilities: any[] = [];
  floors: any[] = [];
  selectedFacilityId: any = null;
  selectedFloorId: any = null;

  floorPlanUrl: string | null = null;
  devices: any[] = [];
  isDraggingEnabled = false;
  private displayImageWidth = 0;
  private displayImageHeight = 0;
  private resizeObserver?: ResizeObserver;

  draggingDevice: string | null = null;
  hoveredDevice: any | null = null;
  zoom: number = 1;

  originalDevicePositions: Map<string, { x: number, y: number }> = new Map();
  modifiedDevices: Set<string> = new Set();

  originalImageWidth = 0;
  originalImageHeight = 0;
  private pubSubSubscription: any;
  isResizing = false;
  isImageReady = false;
  private readonly destroyRef = inject(DestroyRef);
  /** Bumps when simulation state/mappings change so alert labels re-render */
  simulationDisplayVersion = 0;
  /** Room boundaries drawn on the floor plan (read-only on the dashboard). */
  rooms: FloorRoom[] = [];
  /**
   * Live (PubSub) and API alerts in one map keyed by the normalised id, so the same alert
   * can never be listed twice whichever source delivered it (or in which id casing).
   */
  private alertStore = new Map<string, DeviceAlert>();
  /** Unresolved alerts, newest first; rebuilt only when the store changes. */
  alerts: DeviceAlert[] = [];
  /** Keyset cursor for the next API page; null when the API has no more rows. */
  private alertsCursor: string | null = null;
  alertsLoading = false;
  hasMoreAlerts = true;
  alertTotalRecords = 0;
  showAlertsBackToTop = false;
  private readonly alertsPageSize = 20;
  private alertsRequestId = 0;

  constructor(
    private toaster: ToastrService,
    private facilityService: FacilityService,
    private floorService: FloorService,
    private deviceMapService: DeviceMapService,
    private spinner: NgxSpinnerService,
    private permissionService: PermissionService,
    private router: Router ,
    private pubSubService: PubSubService,
    private deviceHealth: DeviceHealthService,
    private toastr: ToastrService,
    private ngZone: NgZone,
    private simulationState: SimulationStateService,
    private mappingService: SimulationTargetMappingService,
    private globalService: GlobalService,
    private floorRoomService: FloorRoomService
  ) { }


  /** Normalised alert id: the API and PubSub may send the same id as a number or a string. */
  private alertKey(id: unknown): string {
    return String(id ?? '').trim().toLowerCase();
  }

  private alertTime(alert: DeviceAlert): number {
    return new Date(alert.timeStamp).getTime() || 0;
  }

  /** Re-derives the visible list from the store: unresolved, newest first, stable on ties. */
  private rebuildAlerts(): void {
    this.alerts = [...this.alertStore.values()]
      .filter(a => a.status !== 'Resolved')
      .sort((a, b) =>
        this.alertTime(b) - this.alertTime(a) ||
        // numeric: ids are bigint identities, so "10" must sort after "9"
        this.alertKey(b.id).localeCompare(this.alertKey(a.id), undefined, { numeric: true })
      );
  }

  get alertBadgeCount(): number {
    const unresolvedLoaded = this.alerts.length;
    return this.alertTotalRecords > 0
      ? this.alertTotalRecords
      : unresolvedLoaded;
  }

  ngOnInit(): void {

    this.getfacilityDropdown();

    const savedFacility = localStorage.getItem('selectedFacilityId');
    const savedFloor = localStorage.getItem('selectedFloorId');

    if (savedFacility) this.selectedFacilityId = savedFacility;
    if (savedFloor) this.selectedFloorId = savedFloor;

    this.simulationState.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.simulationState.enabled) {
          this.mappingService.ensureMappingsLoaded().subscribe(() => {
            this.assignLatestAlertToDevices();
            this.simulationDisplayVersion++;
          });
        } else {
          this.assignLatestAlertToDevices();
          this.simulationDisplayVersion++;
        }
      });

    if (this.simulationState.enabled) {
      this.mappingService.ensureMappingsLoaded().subscribe(() => {
        this.assignLatestAlertToDevices();
        this.simulationDisplayVersion++;
      });
    }
  }

  /** Display label for Device Alerts list (simulation remap when ON) */
  getAlertDisplayCategory(category: string): string {
    if (!this.simulationState.enabled) {
      return category;
    }

    const resolved = this.mappingService.resolveSimulatedLabel(
      category,
      this.simulationState.selectedTargets
    );

    return resolved || category;
  }

  /** Marker / tooltip category — same remap as alerts list */
  getDeviceDisplayCategory(device: any): string {
    const raw = device?.alertCategory || 'Unknown';
    return this.getAlertDisplayCategory(raw);
  }

  getAlertDisplayClass(category: string): string {
    return this.mapCategoryClass(this.getAlertDisplayCategory(category));
  }

  getAlertDisplayColor(category: string): string {
    return this.mapCategoryColor(this.getAlertDisplayCategory(category));
  }

  isDeviceCategory(device: any, ...types: string[]): boolean {
    const display = this.getDeviceDisplayCategory(device);
    return this.globalService.alertTypeMatches(display, ...types);
  }

  /** CSS class for marker: type-ammonia, type-toxigenic, … */
  getDeviceTypeClass(device: any): string {
    if (!device?.alertCategory) {
      return '';
    }
    return 'type-' + this.globalService.getAlertTypeClass(
      this.getDeviceDisplayCategory(device)
    );
  }

  

  ngOnDestroy(): void {

    this.pubSubService.disconnect();

    if (this.pubSubSubscription) {
      this.pubSubSubscription.unsubscribe();
      this.pubSubSubscription = null;
    }

    this.resizeObserver?.disconnect();
  }

 
  getfacilityDropdown() {

    this.facilityService.getFacilityDropdown(null).subscribe((response: any) => {

      if (response.isSuccess) {

        this.facilities = response.data;

        const savedFacilityId = localStorage.getItem('selectedFacilityId');

        if (savedFacilityId) {

          const facility = this.facilities.find(f => f.id === savedFacilityId);

          if (facility) {
            this.selectedFacilityId = facility.id;
            this.onFacilityChange(facility);
          }
        }
      }

    });
  }

  async onFacilityChange(event: any): Promise<void> {

    const selectedFacility = event;
    if (!selectedFacility || !selectedFacility.id) {
      this.selectedFloorId = null;
      this.floors = [];
      this.devices = [];
      this.rooms = [];
      this.resetAlertPaging();
      this.floorPlanUrl = null;
      return;
    }

    this.selectedFacilityId = selectedFacility.id;

    localStorage.setItem('selectedFacilityId', selectedFacility.id);

    const object = {
      facilityId: selectedFacility.id,
      id: null
    };

    this.spinner.show();

    this.floorService.getAllfloor(object).subscribe((response: any) => {

      if (response.isSuccess) {

        this.floors = response.data.list;

        const savedFloorId = localStorage.getItem('selectedFloorId');

        if (savedFloorId) {

          const floor = this.floors.find(f => f.id === savedFloorId);

          if (floor) {
            this.selectedFloorId = floor.id;
            this.onFloorChange(floor);
          }
        }
      }

      this.spinner.hide();
    });
  }

  async onFloorChange(event: any) {

    const floor = event;

    if (!floor?.id) return;

    this.selectedFloorId = floor.id;

    localStorage.setItem('selectedFloorId', floor.id);
    this.devices = [];
    this.rooms = [];
    this.resetAlertPaging();

    this.floorPlanUrl = floor.planImageSasUrl;

    this.loadRooms(floor.id);
    await this.loadDevices(floor.id);
    this.loadAlertsFromApi(floor.id);
    this.connectToPubSub(floor.id);
  }


  private connectToPubSub(floorId: string) {

      // Disconnect previous connections
      this.pubSubService.disconnect();

      if (this.pubSubSubscription) {
        this.pubSubSubscription.unsubscribe();
        this.pubSubSubscription = null;
      }

      // Connect to floor-based PubSub
      this.pubSubService.connect(floorId, 'floor');

      this.pubSubSubscription = this.pubSubService.messages$
      .subscribe((msg: any) => {

        if (!msg || msg.sourceType !== 'floor' || msg.sourceId !== floorId) return;

        const data = msg.data;

        const status = this.globalService.mapAlertStatus(data.Status);

        //  remove resolved — never keep them in the list or badge
        if (status === 'Resolved') {
          const wasCounted = this.alertStore.delete(this.alertKey(data.Id));
          this.rebuildAlerts();

          if (this.alertTotalRecords > 0 && (wasCounted || this.alertTotalRecords > this.alerts.length)) {
            this.alertTotalRecords--;
          }
          return;
        }

        // Ambient Air must not appear in Device Map unresolved alerts (live)
        if (!this.globalService.isListableAlertType(data.Type ?? data.type)) {
          return;
        }

        const newAlert: DeviceAlert = {
          id: data.Id,
          floorName: this.getFloorNameFromApi(data.MacId),
          // Live packets carry no room; use the placed device's API-derived room
          roomName: this.devices.find(d => d.macId === data.MacId)?.roomName ?? '',
          deviceName: '',
          category: data.Type,
          categoryClass: this.mapCategoryClass(data.Type),
          categoryColor: this.mapCategoryColor(data.Type),
          createdOn: this.globalService.parseAlertDate(data.CreatedOn) ?? new Date(),
          macId: data.MacId,
          status: status,
          // Live PubSub: prefer CreatedOn (not TimeStamp) for display time
          date: data.date ?? data.Date ?? data.CreatedOn,
          timeStamp: data.CreatedOn ?? data.TimeStamp
        };

        // Upsert by normalised id: an alert already loaded from the API is updated, not duplicated
        const key = this.alertKey(newAlert.id);
        if (key) {
          const isNew = !this.alertStore.has(key);
          this.alertStore.set(key, newAlert);
          this.rebuildAlerts();

          if (isNew) {
            this.alertTotalRecords++;
          }
        }

        //  update device UI
        const device = this.devices.find(d => d.macId === data.MacId);

        if (device) {
          // Keep raw Type; color/classes come from simulated display when simulation ON
          device.alertCategory = data.Type;
          const displayCategory = this.getAlertDisplayCategory(data.Type);
          device.categoryClass = this.mapCategoryClass(displayCategory);
          device.categoryColor = this.mapCategoryColor(displayCategory);
        }
    });
 }

  /** Loads the floor's room boundaries (FloorRoom/GetByFloor). */
  private loadRooms(floorId: string): void {
    this.floorRoomService.getByFloor(floorId).subscribe({
      next: (response) => {
        // Ignore a late response for a floor the user already switched away from
        if (floorId !== this.selectedFloorId) return;
        this.rooms = response?.isSuccess ? response.data ?? [] : [];
      },
      error: (err) => {
        // Rooms are optional context: log it, keep the map usable without them
        console.error('Failed to load rooms', err);
        if (floorId === this.selectedFloorId) this.rooms = [];
      }
    });
  }

  async loadDevices(floorId: string) {

    this.spinner.show();

    const object = {
      floorId: floorId,
      id: null,
      // Explicit nulls: the API defaults to page 1 of 10, which hid devices beyond 10 on a floor
      pageNumber: null,
      pageSize: null
    };

    this.deviceMapService.getAlldeviceMap(object).subscribe((response: any) => {

      if (response.isSuccess) {

        this.devices = response.data.list.map((device: any) => ({
          ...device,
          status: Math.random() > 0.3 ? 'fine' : 'alert',
          alertCategory: '' 
        }));

         // Re-assign alerts if already loaded
        this.assignLatestAlertToDevices();
      }

      this.spinner.hide();
    });
  }


 private assignLatestAlertToDevices() {

  if (!this.devices?.length) return;

  const alertMap = new Map<string, DeviceAlert>();

  // Prefer newest alert per device (the list is already newest first)
  this.alerts.forEach(alert => {
    if (!alertMap.has(alert.macId)) {
      alertMap.set(alert.macId, alert);
    }
  });

  this.devices.forEach(device => {

    const latestAlert = alertMap.get(device.macId);

    if (latestAlert) {
      // Keep raw category; marker/list remap via getDeviceDisplayCategory when simulation ON
      device.alertCategory = latestAlert.category;

      const displayCategory = this.getAlertDisplayCategory(latestAlert.category);
      device.categoryClass = this.mapCategoryClass(displayCategory);
      device.categoryColor = this.mapCategoryColor(displayCategory);

    } else {
      device.alertCategory = 'Unknown';
      device.categoryClass = 'unknown';
      device.categoryColor = '#6c757d';
    }

  });
}

  // ---------------- DEVICE INTERACTION ----------------

  onImageLoad(event: Event): void {

  this.isImageReady = false;

  const img = event.target as HTMLImageElement;

  requestAnimationFrame(() => {

    requestAnimationFrame(() => {

      this.originalImageWidth = img.naturalWidth;
      this.originalImageHeight = img.naturalHeight;

      this.displayImageWidth = img.offsetWidth;
      this.displayImageHeight = img.offsetHeight;

      this.setupResizeObserver(img);

      this.isImageReady = true;

    });

  });
}

  private setupResizeObserver(
  img: HTMLImageElement
): void {

  this.resizeObserver?.disconnect();

  if (typeof ResizeObserver === 'undefined') {
    return;
  }

  this.ngZone.runOutsideAngular(() => {

    let resizeTimeout: any;

    this.resizeObserver =
      new ResizeObserver(entries => {

        const rect = entries[0]?.contentRect;

        if (!rect) return;

        this.ngZone.run(() => {

          this.isResizing = true;

          this.displayImageWidth = rect.width;
          this.displayImageHeight = rect.height;

          clearTimeout(resizeTimeout);

          resizeTimeout = setTimeout(() => {
            this.isResizing = false;
          }, 80);

        });

      });

    this.resizeObserver.observe(img);
  });
}

  onDeviceMouseDown(event: MouseEvent, deviceId: string) {
    if (!this.isDraggingEnabled) {
    return;
  }
    event.stopPropagation();
    this.draggingDevice = deviceId;
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDraggingEnabled) return;
    if (!this.draggingDevice) return;

    const img = this.floorImage.nativeElement;
    const rect = img.getBoundingClientRect();

    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    const scaleX = this.originalImageWidth / rect.width;
    const scaleY = this.originalImageHeight / rect.height;

    const x = Math.round(clickX * scaleX);
    const y = Math.round(clickY * scaleY);

    this.devices = this.devices.map(device => {

      if (device.id === this.draggingDevice) {

        this.modifiedDevices.add(device.id);

        return { ...device, coordinateX: x, coordinateY: y };
      }

      return device;
    });
  }

  onMouseUp() {
    this.draggingDevice = null;
  }

  openDeleteModal(deviceId: string, event?: Event) {
    if (event) event.stopPropagation();
    this.deleteModal.openModal(deviceId);
  }

  confirmdelete(deviceId: string) {
    this.spinner.show();
    this.deviceMapService.deletedeviceMap(deviceId).subscribe({
      next: (response: any) => {
        this.spinner.hide();
        if (response?.isSuccess) {
          this.devices = this.devices.filter(d => d.id !== deviceId);
          this.modifiedDevices.delete(deviceId);
          this.originalDevicePositions.delete(deviceId);
          this.toaster.success('Device deleted');
        } else {
          this.toaster.error(response?.message || 'Failed to delete device');
        }
      },
      error: (err) => {
        this.spinner.hide();
        this.toaster.error(err?.error?.message || err?.message || 'Failed to delete device');
      }
    });
  }

  // ---------------- UI HELPERS ----------------

  getDevicePosition(device: any) {

      if (!this.displayImageWidth || !this.originalImageWidth) {
      return { x: device.coordinateX, y: device.coordinateY };
    }
    const scaleX = this.displayImageWidth / this.originalImageWidth;
    const scaleY = this.displayImageHeight / this.originalImageHeight;

    return {
      x: device.coordinateX * scaleX,
      y: device.coordinateY * scaleY
    };
  }

  zoomIn() {
    this.zoom = Math.min(this.zoom + 0.2, 3);
  }

  zoomOut() {
    this.zoom = Math.max(this.zoom - 0.2, 0.5);
  }

  get isZoomedOut(): boolean {
    return this.zoom < 1.0;
  }

  resetZoom() {
    this.zoom = 1;
  }

  onDeviceMouseEnter(device: any) {
    this.hoveredDevice = device;
  }

  onDeviceMouseLeave() {
    this.hoveredDevice = null;
  }

  /** Tooltip half-width / height (unscaled px) used to keep it inside the scroll viewport. */
  private readonly tooltipHalfWidth = 170;
  private readonly tooltipHeight = 110;

  /** Screen position of a marker relative to the visible canvas area (after zoom + scroll). */
  private getDeviceViewportPosition(device: any): { x: number, y: number } {
    const pos = this.getDevicePosition(device);
    const container = this.canvasContainer?.nativeElement;
    return {
      x: pos.x * this.zoom - (container?.scrollLeft ?? 0),
      y: pos.y * this.zoom - (container?.scrollTop ?? 0)
    };
  }

  isTooltipFlipped(device: any): boolean {
    return this.getDeviceViewportPosition(device).y < this.tooltipHeight * this.zoom;
  }

  /** Anchor the tooltip to the marker from the inside when it would be clipped at the left/right edge. */
  getTooltipAlign(device: any): 'start' | 'center' | 'end' {
    const container = this.canvasContainer?.nativeElement;
    if (!container) return 'center';

    const x = this.getDeviceViewportPosition(device).x;
    const needed = this.tooltipHalfWidth * this.zoom;

    if (x < needed) return 'start';
    if (container.clientWidth - x < needed) return 'end';
    return 'center';
  }

  isDeviceModified(deviceId: string) {
    return this.modifiedDevices.has(deviceId);
  }

  trackByDeviceId(index: number, device: any) {
    return device.id;
  }

  // Arrow property: *ngFor calls trackBy with `this` = the NgForOf directive, not this component
  trackByAlertId = (_index: number, alert: DeviceAlert): string => this.alertKey(alert.id);

  get fineDevicesCount() {
    return this.devices.filter(d => d.status === 'fine').length;
  }

  get alertDevicesCount() {
    return this.devices.filter(d => d.status === 'alert').length;
  }

  get hasUnsavedChanges() {
    return this.modifiedDevices.size > 0;
  }

  get modifiedDevicesCount() {
    return this.modifiedDevices.size;
  }

  can(action: string) {
    return this.permissionService.can(action);
  }


  openDeviceHealth(device: any): void {
    if (this.draggingDevice) return;

    //Save in localStorage
    localStorage.setItem('selectedDevice', JSON.stringify(device));

    this.spinner.show();

    this.router.navigate(['/device-health'], {
      queryParams: {
        deviceName: device.deviceName,
        macId: device.macId
      }
    });
  }



  private resetAlertPaging(): void {
    this.alertsRequestId++;
    this.alertStore.clear();
    this.alerts = [];
    this.alertsCursor = null;
    this.hasMoreAlerts = true;
    this.alertsLoading = false;
    this.alertTotalRecords = 0;
    this.showAlertsBackToTop = false;
  }

  private mapApiAlert(a: any): DeviceAlert {
    return {
      id: a.id,
      floorName: a.floorName,
      roomName: a.roomName,
      deviceName: a.deviceName,
      category: a.type,
      categoryClass: this.mapCategoryClass(a.type),
      categoryColor: this.mapCategoryColor(a.type),
      createdOn: new Date(a.createdOn),
      status: a.status,
      macId: a.macId,
      date: a.date,
      timeStamp: a.timeStamp
    };
  }

  /**
   * Pure cursor paging: the first page (append=false) sends no cursor and gets the newest
   * alerts plus the total; every later page passes the server's keyset cursor, so alerts
   * inserted meanwhile can't shift pages and the same row is never returned twice.
   */
  private loadAlertsFromApi(floorId: string, append = false) {
    if (!floorId) return;
    if (append && (this.alertsLoading || !this.hasMoreAlerts || !this.alertsCursor)) {
      return;
    }

    const requestId = ++this.alertsRequestId;
    this.alertsLoading = true;

    const filters: AlertFilter = append
      ? { floorId, pageSize: this.alertsPageSize, excludeResolved: true, cursor: this.alertsCursor! }
      : { floorId, pageSize: this.alertsPageSize, excludeResolved: true };

    this.deviceHealth.getAlerts(filters).subscribe({
      next: (res: any) => {
        if (requestId !== this.alertsRequestId) return;

        const { rows, totalRecords, isPaged, nextCursor } = this.globalService.extractAlertPage(res);

        rows
          .filter(
            (a: any) =>
              a.status !== 'Resolved' &&
              this.globalService.isListableAlertType(a.type)
          )
          .forEach((a: any) => {
            // A live copy (possibly newer status) wins over the API row
            const key = this.alertKey(a.id);
            if (key && !this.alertStore.has(key)) {
              this.alertStore.set(key, this.mapApiAlert(a));
            }
          });
        this.rebuildAlerts();

        this.alertsCursor = nextCursor;
        this.hasMoreAlerts = isPaged && !!nextCursor;

        // The total comes with the first page only; live alerts adjust it afterwards
        if (!append) {
          this.alertTotalRecords = isPaged ? Math.max(totalRecords, this.alerts.length) : this.alerts.length;
        } else if (this.alertTotalRecords < this.alerts.length) {
          this.alertTotalRecords = this.alerts.length;
        }

        this.assignLatestAlertToDevices();
        this.alertsLoading = false;
      },
      error: () => {
        if (requestId !== this.alertsRequestId) return;
        // Cursor is unchanged, so the next scroll simply retries the same page
        this.alertsLoading = false;
      }
    });
  }


  private mapCategoryClass(category: string): string {
    return this.globalService.getAlertTypeClass(category);
  }

  private mapCategoryColor(category: string): string {
    return this.globalService.getAlertTypeColor(category);
  }
  
  acknowledgeAlert(alert: DeviceAlert): void {

  this.deviceHealth
    .UpdateAlertStatus(alert.id, 'Acknowledged')
    .subscribe({
      next: (res: any) => {
        console.log('Acknowledged:', res);

        if (res?.isSuccess) {
          //Update UI
          alert.status = 'Acknowledged';

          //Toast
          this.toastr.info('Alert acknowledged', 'Info');
        } else {
          // Backend failure
          this.toastr.error('Failed to acknowledge alert', 'Error');
        }
      },
      error: (err) => {
        console.error('Acknowledge failed:', err);

        // API/network error
        this.toastr.error('Something went wrong while acknowledging alert', 'Error');
      }
    });
  }

  private getFloorNameFromApi(macId: string): string {

    const alert = this.alerts.find(a => a.macId === macId);

    return alert?.floorName || 'Unknown Floor';
  }

 resolveAlert(alert: DeviceAlert): void {

  this.deviceHealth
    .UpdateAlertStatus(alert.id, 'Resolved')
    .subscribe({
      next: (res: any) => {
        console.log('Resolved:', res);

        if (res?.isSuccess) {
          //Remove from UI
          this.alertStore.delete(this.alertKey(alert.id));
          this.rebuildAlerts();
          if (this.alertTotalRecords > 0) {
            this.alertTotalRecords--;
          }

          //Toast
          this.toastr.success('Alert resolved successfully', 'Success');
        } else {
          // Backend failure
          this.toastr.error('Failed to resolve alert', 'Error');
        }
      },
      error: (err) => {
        console.error('Resolve failed:', err);

        // API/network error
        this.toastr.error('Something went wrong while resolving alert', 'Error');
      }
    });
  }

  getOverallUiMessage(): string {

  const criticalCount = this.devices.filter(d => this.isDeviceCriticalForBanner(d)).length;

  const monitoringCount = this.devices.filter(d => this.isDeviceMonitoringForBanner(d)).length;

  switch (this.getOverallUiState()) {

    case 'Critical':

      return criticalCount === 1
        ? 'Toxigenic C-diff signature detected. Immediate infection control required.'
        : `Toxigenic C-diff signature detected in ${criticalCount} rooms. Immediate infection control required.`;

    case 'Monitoring':

      return monitoringCount === 1
        ? 'Elevated gastrointestinal VOC pattern detected. Clinical observation recommended.'
        : `Elevated gastrointestinal VOC pattern detected in ${monitoringCount} rooms. Clinical observation recommended.`;

    default:

      return 'No clinically significant VOC activity detected.';
  }
}

/**
 * Banner Critical/Monitoring use the same simulation display category as markers/list
 * (e.g. Ammonia → C-diff Positive Toxigenic when simulation ON).
 */
private getDeviceBannerCategory(device: any): string {
  return this.getDeviceDisplayCategory(device);
}

private isDeviceCriticalForBanner(device: any): boolean {
  return this.globalService.alertTypeMatches(
    this.getDeviceBannerCategory(device),
    'C-diff Positive Toxigenic'
  );
}

private isDeviceMonitoringForBanner(device: any): boolean {
  return this.globalService.alertTypeMatches(
    this.getDeviceBannerCategory(device),
    'C-diff Positive non-Toxigenic',
    'C-diff Positive Non-Toxigenic',
    'C-diff Negative'
  );
}

private isDeviceNormalForBanner(device: any): boolean {
  const display = this.getDeviceBannerCategory(device);
  const key = this.globalService.getAlertTypeKey(display);
  return (
    key === 'unknown' ||
    key === 'cleaning' ||
    key === 'deep-clean' ||
    this.globalService.alertTypeMatches(display, 'Unknown', 'Cleaning', 'Deep Clean')
  );
}

getOverallUiState(): 'Normal' | 'Monitoring' | 'Critical' {

  if (!this.devices.length) {
    return 'Normal';
  }

  if (this.devices.some(d => this.isDeviceCriticalForBanner(d))) {
    return 'Critical';
  }

  if (this.devices.some(d => this.isDeviceMonitoringForBanner(d))) {
    return 'Monitoring';
  }

  return 'Normal';
}


 // DEVICE STATUS MESSAGE (Which devices, what status)
  // ─────────────────────────────────────────
  getOverallAffectedDevicesText(): string {

    const criticalDevices = this.devices.filter(d => this.isDeviceCriticalForBanner(d));

    const monitoringDevices = this.devices.filter(d => this.isDeviceMonitoringForBanner(d));

    const normalDevices = this.devices.filter(d => this.isDeviceNormalForBanner(d));

    // CRITICAL STATE
    if (criticalDevices.length > 0 && monitoringDevices.length > 0) {
      const criticalNames = criticalDevices.map(d => d.deviceName).join(', ');
      const monitoringNames = monitoringDevices.map(d => d.deviceName).join(', ');
      return `🔴 Critical: ${criticalNames} | 🟡 Monitoring: ${monitoringNames}`;
    }
    
    if (criticalDevices.length > 0) {
      const criticalNames = criticalDevices.map(d => d.deviceName).join(', ');
      return `🔴 Critical devices: ${criticalNames}. Isolation and cleaning required.`;
    }
    
    // MONITORING STATE
    if (monitoringDevices.length > 0) {
      const monitoringNames = monitoringDevices.map(d => d.deviceName).join(', ');
      const normalCount = normalDevices.length;
      
      if (normalCount > 0) {
        return `🟡 Monitoring: ${monitoringNames} | Normal: ${normalCount} devices`;
      }
      return `🟡 Monitoring devices: ${monitoringNames}`;
    }

    // NORMAL STATE
    if (normalDevices.length === this.devices.length) {
      return `All devices operating within normal baseline`;
    }
    
    if (normalDevices.length > 0) {
      return `${normalDevices.length} devices operating normally`;
    }

    return 'No devices on this floor';
  }


  
  /**
   * Prefer API `date` (UTC DD/MM/YYYY HH:mm) for alert footer.
   * Angular date pipe converts the instant to the browser's local timezone.
   */
  getAlertDate(alert: DeviceAlert): Date | null {
    return this.globalService.getAlertDisplayDate(alert);
  }

  getRelativeTime(date: any): string {
    return this.globalService.getRelativeTime(date);
  }

  onAlertsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el) return;

    this.showAlertsBackToTop = el.scrollTop > 180;

    if (
      this.alertsLoading ||
      !this.hasMoreAlerts ||
      !this.selectedFloorId ||
      !this.alertsCursor
    ) {
      return;
    }

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) {
      this.loadAlertsFromApi(this.selectedFloorId, true);
    }
  }

  scrollAlertsToTop(): void {
    this.alertsContainer?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private loadMoreIfAlertsNotScrollable(): void {
    const el = this.alertsContainer?.nativeElement;
    if (
      !el ||
      this.alertsLoading ||
      !this.hasMoreAlerts ||
      !this.selectedFloorId ||
      !this.alertsCursor
    ) {
      return;
    }

    if (el.scrollHeight <= el.clientHeight + 8) {
      this.loadAlertsFromApi(this.selectedFloorId, true);
    }
  }

}


